import { LotConfigStep } from "src/routes/developments/lot-summary/lot-release/enums";
import { PersonalDashboardRoutes } from "src/routes/personal-dashboard/enums";
import { stageCodeToNameMapper, StageUrlSlug } from "src/utils";
import { ReassignPurchaseOrdersStep } from "./developments/reassign-purchase-orders/enums";

export type ProjectParams = { projectId: string };
export type BidItemParams = { bidItemId: string };
export type ProductParams = { productId: string };
export type MaterialParams = { listingId: string; variantId: string };
export type StageParams = { stageSlug: StageUrlSlug };
export type ProjectStageParams = ProjectParams & StageParams;
export type IdOrAddParams = { idOrAdd: string };
export type DevelopmentCommitmentParams = { developmentCommitmentId: string };
export type ChangeEventParams = { changeEventId: string };
export type DevelopmentParams = { developmentId: string };
export type DevelopmentContractParams = { developmentId: string; bidContractRevisionId: string };
export type ItemTemplateItemParams = { itemTemplateId: string; itemTemplateItemId?: string };
export type ScheduleTemplateParams = { scheduleTemplateId: string };
export type LotConfigParams = { projectId?: string; currentStep: LotConfigStep };
export type PlanDetailsParams = DevelopmentParams & { readyPlanId: string };
export type PlanPackageDetailsParams = { planPackageId: string; planPackageVersionId: string };
export type PlanPackageTakeoffParams = PlanPackageDetailsParams & { rpavId: string };
export type DesignCatalogParams = { designPackageId: string; designPackageVersionId: string };
export type ProductOfferingParams = DevelopmentParams & { productOfferingId: string; rpavId?: string };
export type ProductOfferingVersionParams = { productOfferingId: string; rpavId: string };
export type ProductOfferingScopeParams = ProductOfferingVersionParams;
export type ProductOfferingCostCalculatorParams = ProductOfferingVersionParams;
export type ProductOfferingConfigParams = { productOfferingId: string; productOfferingConfigId: string };
export type ProductOfferingConfigApplyParams = { configId: string; projectId: string };
export type BidPackageParams = DevelopmentParams & { bidPackageGroupId: string; bidPackageId: string };
export type ReassignPurchaseOrdersParams = DevelopmentParams & { currentStep: ReassignPurchaseOrdersStep };
export type LotConfigDetailParams = DevelopmentParams & {
  projectReadyPlanConfigId: string;
  // It receives the project id from the site condition page when should auto-open the project site condition super drawer
  projectIdFromSiteCondition: string;
};
export type WarrantyParams = { warrantyTicketId: string };
/** I.e. the `/libraries/design-packages/rp:2/detail/tli:3/tli:4. */
export type DesignPackageParams = { designPackageId: string; placeholderTliId: string; productTliId?: string };
export type LaborCostParams = { globalPlanTaskId: string };

export const idOrAdd = ":idOrAdd";
const stageParam = ":stageSlug";

export const invoicesPath = "/invoices";
export const billsPath = "/bills";
export const billDetailsPath = `/bills/:billId`;
export const createBillPath = `/bills/${idOrAdd}`;
export const purchaseOrdersPath = "/purchase-orders";
export const invoiceTermsPath = "/invoice-terms";
export const invoiceTermPath = `${invoiceTermsPath}/${idOrAdd}`;
export const lienWaiversPath = "/lien-waivers";
export const tradePartnersPath = "/trade-partners";
const tradePartnerPath = `${tradePartnersPath}/${idOrAdd}`;
export const paymentTermsPath = `${tradePartnersPath}/payment-terms`;
export const tradePartnerPaths = {
  tradePartner: tradePartnerPath,
  details: `${tradePartnerPath}/details`,
  contacts: `${tradePartnerPath}/contacts`,
  contact: `${tradePartnerPath}/contacts/:tpContactId`,
  reviews: `${tradePartnerPath}/reviews`,
  commitments: `${tradePartnerPath}/commitments`,
  developmentContracts: `${tradePartnerPath}/development-contracts`,
  requirements: `${tradePartnerPath}/requirements`,
};
export const tradeCategoryLeadTimesPath = `${tradePartnersPath}/tradeCategoryLeadTimes`;

export const tradesPath = "/trades";

/** Strip the `/trades/` prefix for nested routes under TradesLayout. */
export function relativeTradesPath(path: string): string {
  const prefix = `${tradesPath}/`;
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path === tradesPath) {
    return "";
  }
  return path;
}

export const tradesPurchaseOrdersPath = `${tradesPath}/purchase-orders`;
export const tradesTradePartnersPath = `${tradesPath}/trade-partners`;
export const tradesBidContractsPath = `${tradesPath}/bid-contracts`;

export const bidPackagesPath = `${tradesPath}/bid-packages`;
export const bidPackagePath = `${bidPackagesPath}/${idOrAdd}`;
export const createBidPackagePath = `${bidPackagesPath}/add`;
export const bidPackagesEditPath = `${bidPackagesPath}/edit/:bidPackageGroupId`;
export const bidPackageDetailsPath = `${bidPackagesPath}/:bidPackageGroupId/:bidPackageId`;
export const planBasedBidPackagesPath = `${bidPackagesPath}/plan-based-bid-package`;
export const createPlanBasedBidPackagePath = `${planBasedBidPackagesPath}/add`;
export const planBasedBidPackagesEditPath = `${planBasedBidPackagesPath}/edit/:bidPackageGroupId`;
// `:bidderId` is a `BidPackageBidder.id`, which the backend sets to the underlying
// `BidPackageRequest.id` (bpr:) for trade-partner bidders and the `BidContract.id` (bc:)
// for the internal-estimate bidder. Keep this regex in sync with that invariant.
export const enterPlanBasedBidPath = `${planBasedBidPackagesPath}/:bidPackageId/enter-bid/:bidderId`;

export const documentsPath = "/documents";

export const expensesPath = "/expenses";

export const searchPath = "/search";

const adminPath = "/admin";
export const adminPaths = {
  admin: adminPath,
  projects: `${adminPath}/projects`,
  users: `${adminPath}/users`,
  items: `${adminPath}/items`,
  costCodes: `${adminPath}/costCodes`,
  featureFlags: `${adminPath}/feature-flags`,
  userGroups: `${adminPath}/user-groups`,
  locations: `${adminPath}/locations`,
  featureTypes: `${adminPath}/feature-types`,
  infrastructure: `${adminPath}/infrastructure`,
};

/**
 * Below are temporary "relative__Path" helpers to support the transition to React Router v7.
 * These will be removed once we have fully migrated to React Router v7, in favor of using <Outlet />
 */

/** Strip the `/admin/` prefix for nested routes under Admin. */
export function relativeAdminPath(path: string): string {
  const prefix = `${adminPath}/`;
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path === adminPath) {
    return "";
  }
  return path;
}

export const projectsPath = "/projects";

const projectPath = `${projectsPath}/:projectId`;

/** Strip the `/projects/:projectId/` prefix for nested routes under ProjectLayout. */
export function relativeProjectPath(path: string): string {
  const prefix = `${projectPath}/`;
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path === projectPath) {
    return "";
  }
  return path;
}

export const developmentsPath = "/developments";
export const developmentPath = `${developmentsPath}/:developmentId`;

/** Strip the `/developments/:developmentId/` prefix for nested routes under DevelopmentLayout. */
export function relativeDevelopmentPath(path: string): string {
  const prefix = `${developmentPath}/`;
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path === developmentPath) {
    return "";
  }
  return path;
}

export const developmentPaths = {
  development: developmentPath,
  legacyScheduleRoute: `${developmentPath}/schedule`,
  lotSchedules: `${developmentPath}/lot-schedules`,
  developmentSchedule: `${developmentPath}/development-schedule`,
  jobLogs: `${developmentPath}/job-logs`,
  jobLogsGallery: `${developmentPath}/job-logs/gallery`,
  jobLogNoteDrawer: `${developmentPath}/job-logs/:jobLogId/:jobLogNoteId`,
  detail: `${developmentPath}/detail`,
  documents: `${developmentPath}/documents`,
  team: `${developmentPath}/team`,
  drops: `${developmentPath}/drops`,
  allLots: `${developmentPath}/cohorts`,
  bidPackages: `${developmentPath}/bid-packages`,
  bidPackage: `${developmentPath}/bid-packages/:bidPackageId`,
  contract: `${developmentPath}/procurement/:bidContractRevisionId`,
  contractLineItems: `${developmentPath}/procurement/:bidContractRevisionId/line-items`,
  unitPricing: `${developmentPath}/procurement/:bidContractRevisionId/unit-pricing`,
  contractOverview: `${developmentPath}/procurement/:bidContractRevisionId/overview`,
  contractPurchaseOrders: `${developmentPath}/procurement/:bidContractRevisionId/purchase-orders`,
  lotConfig: `${developmentPath}/lot-config`,
  lotSummary: `${developmentPath}/lot-summary`,
  lotSequenceSheet: `${developmentPath}/lot-summary/sequence-sheet`,
  lotDetails: `${developmentPath}/lot-summary/sequence-sheet/:projectReadyPlanConfigId`,
  lotSiteConditionDetails: `${developmentPath}/lot-summary/sequence-sheet/:projectIdFromSiteCondition`,
  lotSummaryRelease: `${developmentPath}/lot-summary/release`,
  procurement: `${developmentPath}/procurement`,
  purchaseOrders: `${developmentPath}/purchase-orders`,
  reassignPurchaseOrders: `${developmentPath}/reassign-purchase-orders`,
  scopeTemplate: `${developmentPath}/templates/:itemTemplateId`,
  scopeTemplateItem: `${developmentPath}/templates/:itemTemplateId/:itemTemplateItemId`,
  scopeTemplateCostReport: `${developmentPath}/templates/cost/:itemTemplateId`,
  scopeTemplates: `${developmentPath}/templates`,
  reviewTemplates: `${developmentPath}/templates/review`,
  planAndOptions: `${developmentPath}/plan-and-options`,
  productOfferings: `${developmentPath}/product-offerings`,
  productOffering: `${developmentPath}/product-offerings/:productOfferingId/:rpavId?`,
  productOfferingApplyToProject: `${developmentPath}/product-offerings/:productOfferingId/:rpavId/apply`,
  productOfferingVersionHistory: `${developmentPath}/product-offerings/:productOfferingId/history`,
  productOfferingSpecificConfigs: `${developmentPath}/product-offerings/:productOfferingId/product-offering-configs`,
  productOfferingConfigs: `${developmentPath}/product-offering-configs`,
  productOfferingSpecificConfigsCompare: `${developmentPath}/product-offering-configs/:productOfferingId/compare`,
  productOfferingConfigsCompare: `${developmentPath}/product-offering-configs/compare`,
  planDetails: `${developmentPath}/plan-and-options/:readyPlanId`,
  planData: `${developmentPath}/plan-and-options/:readyPlanId/planData`,
  optionData: `${developmentPath}/plan-and-options/:readyPlanId/optionData`,
  activity: `${developmentPath}/plan-and-options/:readyPlanId/activity`,
  editOptions: `${developmentPath}/plan-and-options/:readyPlanId/edit-options`,
  taskLookaheadReport: `${developmentPath}/reports/task-lookahead`,
  settings: `${developmentPath}/settings`,

  featureFlagTest: `${developmentPath}/feature-flag-test`,

  changeLog: `${developmentPath}/change-log`,
  changeLogDetails: `${developmentPath}/change-log/:changeRequestId`,
};

export const costMappingPath = "/schedule-cost-mapping/:developmentId";

/** Full route of the Development Cost Map wizard , currentStep */

export const librariesPath = `/libraries`;

/** Strip the `/libraries/` prefix for nested routes under LibrariesLayout. */
export function relativeLibrariesPath(path: string): string {
  const prefix = `${librariesPath}/`;
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path === librariesPath) {
    return "";
  }
  return path;
}

export const globalOptionsPath = `${librariesPath}/global-options`;
export const globalOptionPath = `${globalOptionsPath}/${idOrAdd}`;
export const globalOptionsLegacyPath = `${librariesPath}/global-options-legacy`;
export const materialsCatalogPath = `${librariesPath}/material-catalog`;
export const materialCreatePath = `${materialsCatalogPath}/add`;
export const materialDetailsPath = `${materialsCatalogPath}/:variantId`;
export const materialCostPath = `${materialDetailsPath}/cost`;
export const installTasksPath = `${materialsCatalogPath}/install-tasks`;
export const productCatalogPath = `${librariesPath}/product-catalog`;
export const productCreatePath = `${productCatalogPath}/add`;
export const productDetailsPath = `${productCatalogPath}/:productId`;
export const itemCatalogPath = `${librariesPath}/item-catalog`;
export const bidItemCreatePath = `${itemCatalogPath}/add`;
export const bidItemDetailsPath = `${itemCatalogPath}/:bidItemId`;
export const templatesPath = `/templates`;
export const itemTemplatesPath = `${librariesPath}${templatesPath}/items`;
export const itemTemplatePath = `${itemTemplatesPath}/${idOrAdd}`;
export const itemTemplateItemPath = `${itemTemplatesPath}/:itemTemplateId/:itemTemplateItemId`;
export const scheduleTemplatesPath = `${librariesPath}${templatesPath}/schedule-templates`;
export const scheduleTemplatePath = { scheduleTemplate: `${scheduleTemplatesPath}/:scheduleTemplateId` };

export const catalogTasksPath = `${librariesPath}/task-catalog`;
export const catalogTaskPath = `${catalogTasksPath}/${idOrAdd}`;
export const constraintItemsCatalogPath = `${catalogTasksPath}/constraint-and-allowance-catalog`;
export const catalogTaskFolderPath = `${catalogTasksPath}/:folderId`;
export const catalogTaskLaborCosts = `${catalogTasksPath}/:globalPlanTaskId/labor-costs`;

export const milestonesCatalogPath = `${librariesPath}/milestone-catalog`;
export const milestoneCatalogPath = `${milestonesCatalogPath}/${idOrAdd}`;

export const designCatalogsPath = `${librariesPath}/design-packages`;

// designCatalog inherits from ReadyPlan so its ID per joist-config is `rp:123` not the typical `dc:123`
export const designCatalogPath = `${designCatalogsPath}/:designPackageId/:designPackageVersionId`;
export const designPackageAddToSlotPath = `${designCatalogPath}/add-to`;
export const designPackageFinishSchedulePath = `${designCatalogPath}/finish-schedule`;
export const designPackageCompareModePath = `${designCatalogPath}/compare-mode`;
export const designPackageProductCardPath = `${designCatalogPath}/detail/:placeholderTliId/:productTliId?`;
export const designPackageProductSearchPath = `${designCatalogPath}/search/:placeholderTliId/:productTliId?`;
export const designPackageAddProductPath = `${designCatalogPath}/add-product/:placeholderTliId/:productTliId?`;
export const designPackageVersionHistoryPath = `${designCatalogPath}/history`;
export const designPackagePersonalizationGroupsPath = `${designCatalogPath}/personalization-groups`;
export const designPackageEditOptionPath = `${designCatalogPath}/options/${idOrAdd}`;
export const designPackageRequiredOptionsPath = `${designCatalogPath}/required-options`;

export const planPackagesPath = `${librariesPath}/plan`;
export const planPackagePath = `${planPackagesPath}/:planPackageId/:planPackageVersionId`;
export const planPackageDetailsOverview = `${planPackagePath}/overview`;
export const planPackageDetailsOptionData = `${planPackagePath}/option-data`;
export const planPackageDetailsActivity = `${planPackagePath}/activity`;
export const planPackageDetailsDocuments = `${planPackagePath}/documents`;
export const planPackageEditPath = `${planPackagesPath}/edit/:planPackageId?/:planPackageVersionId?`;
export const planPackageTakeoffsPath = `${planPackagePath}/takeoffs`;
export const planPackageIncomingSlotReviewPath = `${planPackagePath}/incoming-slot-review`;
export const planPackageVersionHistoryPath = `${planPackagePath}/history`;

export const globalProductOfferingsPath = `${librariesPath}/product-offerings`;
export const productOfferingCreatePath = `${globalProductOfferingsPath}/add`;
export const productOfferingPath = `${globalProductOfferingsPath}/${idOrAdd}`;
export const productOfferingEditPath = `${globalProductOfferingsPath}/edit/:productOfferingId/:rpavId`;
export const productOfferingScopePath = `${globalProductOfferingsPath}/:productOfferingId/scope/:rpavId`;
export const productOfferingCostCalculatorPath = `${globalProductOfferingsPath}/:productOfferingId/cost-calculator/:rpavId`;
export const productOfferingConfigPricingTracePath = `${globalProductOfferingsPath}/checkout-pricing-trace/:configId`;

export const globalProductOfferingConfigsPath = `${librariesPath}/product-offering-configs`;
export const productOfferingConfigCreatePath = `${globalProductOfferingConfigsPath}/add`;
export const productOfferingConfigPath = `${globalProductOfferingConfigsPath}/:productOfferingId/${idOrAdd}`;
export const productOfferingConfigEditPath = `${globalProductOfferingConfigsPath}/:productOfferingId/edit/:productOfferingConfigId`;
export const productOfferingConfigsComparePath = `${globalProductOfferingConfigsPath}/compare`;

export const projectItemScopeOfWorkPath = `/pdf/projectItem`;
export const tradeScopeOfWorkPath = `/pdf/changeEventLineItem`;
export const scheduleOfValuesPath = `/pdf/schedule-of-values`;
export const homeownerChangeOrderPath = `/pdf/homeowner-change-order`;
export const estimateExportPath = `/pdf/estimate`;
export const scheduleExportPath = `/pdf/schedule/:scheduleId`;
export const tradeSpecificAgreementPath = `/pdf/trade-specific-agreement`;
export const developmentCommitmentPdfPath = `/pdf/development-commitment`;
export const commitmentPdfPath = `/pdf/commitment`;
export const invoicePandaDocPdfPath = `/pdf/invoicePandaDoc`;
export const invoicePdfPath = `/pdf/invoice`;
export const lienWaiverPdfPath = `/pdf/lien-waivers`;
export const toDoPdfPath = `/pdf/to-dos`;
export const commitmentChangeOrderPdfPath = `/pdf/commitment-change-order`;
export const developmentContractPdfPath = `/pdf/development-contract`;
export const bidCommitmentPdfPath = `/pdf/bid-commitment`;
export const bidCommitmentChangeOrderPdfPath = `/pdf/bid-commitment-change-order`;
export const lotSequenceSheetPdfPath = `/pdf/lot-sequence-sheet`;
export const lotSummaryDetailsPdfPath = `/pdf/lot-summary-details`;
export const billPdfPath = `/pdf/bill`;
export const finishSchedulePdfPath = `/pdf/finish-schedule`;
export const finishScheduleVisualModePdfPath = `/pdf/finish-schedule-visual-mode`;
export const projectFinishScheduleVisualModePdfPath = `/pdf/project-finish-schedule-visual-mode`;
export const projectFinishSchedulePdfPath = `/pdf/project-finish-schedule`;
export const tradesPunchlistPdfPath = `/pdf/trades-punchlist`;

export const developmentCommitmentsPath = "/development-commitments";
export const developmentCommitmentsPaths = {
  developmentCommitments: developmentCommitmentsPath,
  developmentCommitment: `${developmentCommitmentsPath}/:developmentCommitmentId`,
};

export const personalDashboardPaths: Record<string, string> = {
  base: "/personal-dashboard",
  dashboard: "/personal-dashboard/:section?",
  actions: `/personal-dashboard/${PersonalDashboardRoutes.ACTIONS}`,
  upcoming: `/personal-dashboard/${PersonalDashboardRoutes.UPCOMING}`,
  toDos: `/personal-dashboard/${PersonalDashboardRoutes.TO_DOS}`,
  toDoModal: `/personal-dashboard/${PersonalDashboardRoutes.TO_DOS}/:toDoId`,
  approvals: `/personal-dashboard/${PersonalDashboardRoutes.APPROVALS}`,
  activity: `/personal-dashboard/${PersonalDashboardRoutes.ACTIVITY}`,
};

export const inboxPaths: Record<string, string> = { base: "/inbox" };

export const reassignPurchaseOrdersPath = `${developmentPaths.reassignPurchaseOrders}/:currentStep`;
export const reassignPurchaseOrdersPaths: Record<ReassignPurchaseOrdersStep, string> = {
  [ReassignPurchaseOrdersStep.SELECT]: `${developmentPaths.reassignPurchaseOrders}/${ReassignPurchaseOrdersStep.SELECT}`,
  [ReassignPurchaseOrdersStep.RELEASE]: `${developmentPaths.reassignPurchaseOrders}/${ReassignPurchaseOrdersStep.RELEASE}`,
};

export const projectPaths = {
  project: projectPath,
  dashboard: `${projectPath}/dashboard`,
  team: `${projectPath}/team`,
  settings: `${projectPath}/settings`,
  items: `${projectPath}/items`,
  featureFlagTest: `${projectPath}/feature-flag-test`,
  bill: `${projectPath}/bills/:billId?`,

  budgetBase: `${projectPath}/budget`,
  budget: `${projectPath}/budget/${stageParam}`,
  budgetMetrics: `${projectPath}/budget-metrics`,
  budgetReallocation: `${projectPath}/budget-reallocations/${stageParam}/${idOrAdd}`,

  scheduleBase: `${projectPath}/schedule`,
  schedule: `${projectPath}/schedule/${stageParam}`,
  dynamicSchedules: `${projectPath}/dynamicSchedule`,

  contracts: `${projectPath}/homeowner-contracts`,
  contract: `${projectPath}/homeowner-contracts/:contractId`,
  contractChangeOrder: `${projectPath}/homeowner-contracts/change-order/:changeOrderId?`,
  contractChangeOrderDraws: `${projectPath}/homeowner-contracts/change-order/:changeOrderId\\??tab=draws`,
  contractDraws: `${projectPath}/homeowner-contracts/:contractId\\?tab=draws`,
  contractInvoiceSchedule: `${projectPath}/homeowner-contracts/:contractId\\?tab=invoice`,

  commitments: `${projectPath}/commitments`,
  commitment: `${projectPath}/commitments/${idOrAdd}`,
  commitmentChangeOrder: `${projectPath}/commitments/:commitmentId/change-order/${idOrAdd}`,
  releaseToTrades: `${projectPath}/release-to-trades`,

  drops: `${projectPath}/drops`,

  homeownerNotes: `${projectPath}/homeowner-notes`,

  invoices: `${projectPath}/invoices`,
  invoice: `${projectPath}/invoices/${idOrAdd}`,
  invoiceOld: `${projectPath}/invoices/old/${idOrAdd}`,

  estimates: `${projectPath}/estimates`,
  estimate: `${projectPath}/estimates/:estimateId`,

  expenses: `${projectPath}/expenses`,

  finishSchedule: `${projectPath}/finish-schedule`,
  /*
    Individual redirect paths from:
    - /selections/:stage
    - /selections/:stage/:projectItemId
    to:
    - /pre-con-services
    - /pre-con-services/:projectItemId
    - /specs-and-selection
    - /specs-and-selection/:projectItemId

    @deprecated use either preConServices or specsAndSelections paths
  */
  selectionsBase: `${projectPath}/selections`,
  selectionsPreCon: `${projectPath}/selections/${stageCodeToNameMapper.PRE_CON_EXECUTION}`,
  selectionsPreConDetails: `${projectPath}/selections/${stageCodeToNameMapper.PRE_CON_EXECUTION}/:projectItemId`,
  selectionsConstruction: `${projectPath}/selections/${stageCodeToNameMapper.CONSTRUCTION}`,
  selectionsConstructionDetails: `${projectPath}/selections/${stageCodeToNameMapper.CONSTRUCTION}/:projectItemId`,

  preConServices: `${projectPath}/pre-con-services`,
  preConServicesDetail: `${projectPath}/pre-con-services/:projectItemId`,

  lotSummary: `${projectPath}/lot-summary`,

  specsAndSelections: `${projectPath}/specs-and-selections`,
  specsAndSelectionsDetail: `${projectPath}/specs-and-selections/:projectItemId`,

  toDos: `${projectPath}/to-dos`,
  toDoModal: `${projectPath}/to-dos/:toDoId`,

  jobLogs: `${projectPath}/job-logs`,
  jobLogsGallery: `${projectPath}/job-logs/gallery`,
  jobLogNoteDrawer: `${projectPath}/job-logs/:jobLogId/:jobLogNoteId`,

  documents: `${projectPath}/documents`,

  changeEvents: `${projectPath}/change-events`,
  changeEvent: `${projectPath}/change-events/:changeEventId`,
  changeEventLineItems: `${projectPath}/change-events/:changeEventId/line-items`,
  changeEventOverview: `${projectPath}/change-events/:changeEventId/overview`,
  changeEventEstimates: `${projectPath}/change-events/:changeEventId/estimates`,
  changeEventCommitments: `${projectPath}/change-events/:changeEventId/commitments`,
  changeEventHistory: `${projectPath}/change-events/:changeEventId/history`,
  changeEventRpos: `${projectPath}/change-events/:changeEventId/rpos`,
  changeEventLineItem: `${projectPath}/change-events/:changeEventId/line-items/:celiId`,
  changeEventLineItemHistory: `${projectPath}/change-events/:changeEventId/line-items/:celiId/history`,
  changeEventLineItemSelection: `${projectPath}/change-events/:changeEventId/line-items/:celiId/selection`,
  changeLog: `${projectPath}/change-log`,
  changeLogDetails: `${projectPath}/change-log/:changeRequestId`,

  productOfferings: `${projectPath}/product-offerings`,
  productOffering: `${projectPath}/product-offerings/:productOfferingId/:rpavId?`,
  productOfferingApplyToProject: `${projectPath}/product-offerings/:productOfferingId/:rpavId/apply`,
  productOfferingVersionHistory: `${projectPath}/product-offerings/:productOfferingId/history`,
  productOfferingSpecificConfigs: `${projectPath}/product-offerings/:productOfferingId/product-offering-configs`,
  productOfferingConfigs: `${projectPath}/product-offering-configs`,
  productOfferingSpecificConfigsCompare: `${projectPath}/product-offering-configs/:productOfferingId/compare`,
  productOfferingConfigsCompare: `${projectPath}/product-offering-configs/compare`,
  productOfferingConfigAppliable: `${projectPath}/appliable-product-offering-configs`,
  productOfferingConfigApply: `${projectPath}/apply-product-offering-config/:configId`,

  bidPackages: `${projectPath}/bid-packages`,

  bidContracts: `${projectPath}/bid-contracts`,
  bidContract: `${projectPath}/bid-contracts/:bidContractRevisionId`,
  bidContractOverview: `${projectPath}/bid-contracts/:bidContractRevisionId/overview`,
  bidContractPlanPricing: `${projectPath}/bid-contracts/:bidContractRevisionId/plan-pricing`,
  bidContractUnitPricing: `${projectPath}/bid-contracts/:bidContractRevisionId/unit-pricing`,
  bidContractPurchaseOrders: `${projectPath}/bid-contracts/:bidContractRevisionId/purchase-orders`,
};

export const releaseToTradesPaths: Record<LotConfigStep, string> = {
  [LotConfigStep.CONFIRM]: `${projectPaths.releaseToTrades}/${LotConfigStep.CONFIRM}`,
  [LotConfigStep.REVIEW]: `${projectPaths.releaseToTrades}/${LotConfigStep.REVIEW}`,
};

const changeLogPath = "/change-log";
export const changeLogPaths = {
  base: changeLogPath,
  details: `${changeLogPath}/:changeRequestId`,
  changeTypes: `${changeLogPath}/change-types`,
  businessFunctions: `${changeLogPath}/functions`,
  businessFunctionDetails: `${changeLogPath}/functions/:businessFunctionId`,
};

// Using this to filter out projects paths in the `suggestablePaths` array below rather than listing out all suggestable project paths.
const nonSuggestableProjectPaths = [projectPaths.items];

export const suggestablePaths = [
  billsPath,
  developmentCommitmentsPath,
  documentsPath,
  invoicesPath,
  expensesPath,
  personalDashboardPaths.dashboard,
  personalDashboardPaths.toDos,
  projectsPath,
  ...Object.values(projectPaths).reduce(
    (acc, path) => (nonSuggestableProjectPaths.includes(path) ? acc : acc.concat(path)),
    [] as string[],
  ),
  itemTemplatesPath,
  itemTemplatePath,
  tradePartnersPath,
];

const warrantyPath = "/warranty";
export const warrantyPaths = { base: warrantyPath, details: `${warrantyPath}/:warrantyTicketId` };

export const dynamicSchedulesPath = {
  // Draft routes using shared draft schedule store
  draftMode: `${projectPaths.dynamicSchedules}/draft`,
  draftNewTask: `${projectPaths.dynamicSchedules}/draft/new`,
  draftPublish: `${projectPaths.dynamicSchedules}/draft/publish`,
  draftCalendar: `${projectPaths.dynamicSchedules}/draft/calendar`,
  draftGantt: `${projectPaths.dynamicSchedules}/draft/gantt`,
  draftLookahead: `${projectPaths.dynamicSchedules}/draft/lookahead`,
  draftMilestone: `${projectPaths.dynamicSchedules}/draft/milestones`,
  // Detail routes that doo not rely on store
  details: `${projectPaths.dynamicSchedules}/:planTaskId`,
  materials: `${projectPaths.dynamicSchedules}/:planTaskId/materials`,
  materialsDetails: `${projectPaths.dynamicSchedules}/:planTaskId/materials/:variantId`,
  milestoneDetails: `${projectPaths.dynamicSchedules}/milestones/:planMilestoneId`,
};

export const financesPath = { bill: `${billsPath}/:billId` };
